/**
 * A stale chunk after a deploy, handled by `DefaultCatchBoundary`'s one-shot reload; the only patterns matched
 * against the CAUSE chain too. One event in every browser's wording: WebKit's `cross-origin script load denied`
 * is the same failed module fetch, not our CORS config, and covering Chrome's alone is how TEC-3740 half-held
 * until TEC-3839. A genuine CDN CORS fault is dropped too, accepted: it would fail for many real sessions.
 */
const VERSION_SKEW_ERROR_PATTERNS = [
	'failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'importing a module script failed',
	'chunkloaderror',
	'cross-origin script load denied',
];

const NON_ACTIONABLE_ERROR_PATTERNS = [
	...VERSION_SKEW_ERROR_PATTERNS,
	// Third-party cross-origin scripts (reCAPTCHA, GA, Adyen).
	'script error.',
	'invalid site key or not loaded in api.js',
	'recaptcha not loaded',
	'recaptcha has already been rendered',
	// reCAPTCHA's challenge never answered; the stack is entirely gstatic.com/recaptcha (TEC-3453).
	'recaptcha timeout',
	// Outlook SafeLinks' scanning WebView injects a script that fails like this (TEC-3454, TEC-3327).
	'object not found matching id',
	// An injected `appendChild` patch stringifying a mounted node, cyclic through React's fiber key (TEC-3452).
	// Matched on the key, not "circular structure", so our own circular-JSON bug still reports.
	'__reactfiber',
	// Crypto-wallet extensions' injected provider; we never touch `window.ethereum` (TEC-3653).
	'window.ethereum',
	// Firefox-iOS and Brave-iOS inject a `__firefox__` reader helper that fails on pages it can't parse (TEC-3654).
	'__firefox__',
	// Extension-only API, absent from our source; it arrives stackless, past the frame gate (TEC-3757).
	'runtime.sendmessage',
	// Extensions and page translators mutating the DOM under React.
	"failed to execute 'insertbefore' on 'node'",
	"failed to execute 'removechild' on 'node'",
	'the node before which the new node is to be inserted is not a child of this node',
	'the object can not be found here',
	"cannot read properties of null (reading 'parentnode')",
	// Transient aborts: the visitor navigated away or the connection dropped mid-stream.
	'aborterror',
	'the operation was aborted',
	// A superseded view transition rejecting `.ready`; benign (TEC-3546).
	'transition was aborted',
	'the connection was closed',
	'stream lifetime exceeded',
	// A benign observer feedback loop the browser reports as an error.
	'resizeobserver loop',
	// React on a synthetic null-`detail` event from an injected script (TEC-3760).
	// docs/notes/portals/frontend-error-filter.md
	"cannot use 'in' operator to search for 'data' in",
];

/**
 * The stack shape of runaway recursion (many frames, very few distinct), which separates a genuine overflow
 * from an error that merely mentions the phrase. The case it covers is seroval's recursive serialization
 * overflowing WebKit's smaller stack (TEC-3592): TEC-3452's injected-bot mechanism, and nothing of ours, since
 * every campaign-route query is at most depth 9.
 */
export function isStackOverflowRecursionCycle(stack: string | undefined): boolean {
	if (!stack) return false;
	const named: string[] = [];
	for (const raw of stack.split('\n')) {
		const line = raw.trim();
		if (!line) continue;
		let name: string | undefined;
		if (line.startsWith('at ')) {
			name = line.slice(3).split(' ')[0];
			if (!name || name.startsWith('(')) name = undefined;
		} else {
			const at = line.indexOf('@');
			if (at > 0) name = line.slice(0, at);
		}
		if (name) named.push(name);
	}
	return named.length >= 20 && new Set(named).size <= 3;
}

/** Every script we ship is served under it, on the CDN or same-origin, so it is what ties a frame to our code. */
const APP_CODE_FRAME_MARKER = '/assets/';

/**
 * Vite's dev-server paths, without which every dev error reads as third-party and is never sent (TEC-3869).
 * DEV-only, so the build keeps exactly the `/assets/` rule TEC-3648, TEC-3649 and TEC-3650 depend on.
 */
const DEV_APP_CODE_FRAME_MARKERS = ['/src/', '/@fs/', '/node_modules/.vite/'];

function isAppCodeFrame(frame: string): boolean {
	if (frame.includes(APP_CODE_FRAME_MARKER)) return true;
	return import.meta.env.DEV && DEV_APP_CODE_FRAME_MARKERS.some((marker) => frame.includes(marker));
}

const V8_FRAME = /^at\s/;
/**
 * Strict after the `@` so a message containing one (`foo@bar.com`) is not a frame; the name may hold spaces
 * because WebKit emits `global code@…`.
 */
const WEBKIT_FRAME = /^.*@(\[native code\]|[a-z][a-z0-9+.-]*:\/\/\S*|)$/i;

function stackFrames(stack: string): string[] {
	return stack
		.split('\n')
		.map((line) => line.trim())
		.filter((line) => line !== '' && (V8_FRAME.test(line) || WEBKIT_FRAME.test(line)));
}

/**
 * Injected scripts throw minified names that change every rebuild (TEC-3648, TEC-3649, TEC-3650: no URL, or a
 * frame at `<document>:415` of a 51-line document), so the only stable tell is that no frame is ours. Our own
 * errors keep their frames because chunks load in CORS mode against `Access-Control-Allow-Origin: *` (TEC-3651).
 * A frameless stack proves nothing and still reports. Accepted cost: an error from the inline SSR shim scripts
 * is dropped too; a real fault there also surfaces through bundle frames.
 */
export function isUnattributableThirdPartyError(stack: string | undefined): boolean {
	if (!stack) return false;
	const frames = stackFrames(stack);
	if (frames.length === 0) return false;
	return !frames.some(isAppCodeFrame);
}

/**
 * A failed Apple Pay merchant validation: Adyen's `onvalidatemerchant` calls `session.abort()` before
 * `onError`, WebKit's abort throws `InvalidAccessError` on a closed sheet, and the throw escapes as an unhandled
 * rejection that stops `onError` from ever running, leaving the donor at a closed sheet with no feedback
 * (TEC-3637). The checkout route reports it with payment context and the global handler in `router.tsx` skips
 * it, so it is still ticketed, once. Gated on `abort@[native code]` because the message is WebKit's generic one.
 */
export function isApplePaySessionAbortError(name: string, message: string, stack: string | undefined): boolean {
	if (!stack) return false;
	return (
		name.toLowerCase().includes('invalidaccesserror') &&
		message.toLowerCase().includes('does not support the operation or argument') &&
		stack.toLowerCase().includes('abort@[native code]')
	);
}

/**
 * The Firefox-engine rejection of `releaseLock()` in TanStack's SSR-query `hydrate` read loop, raised only once the
 * stream is done: genuine browsers mark that promise handled, and the only reporter was automation (TEC-4028).
 * Gated on `options.hydrate`, a property name that survives minification, so a released reader elsewhere still reports.
 */
export function isReleasedQueryStreamReader(name: string, message: string, stack: string | undefined): boolean {
	if (!stack) return false;
	return (
		name.toLowerCase().includes('typeerror') &&
		message.toLowerCase().includes('readablestream reader was released') &&
		stack.includes('options.hydrate')
	);
}

/**
 * The global handler skips the Apple Pay abort only while the checkout's listener is mounted, so one raised
 * elsewhere or after unmount is still reported by someone. Module state is safe: read only from event handlers.
 */
let applePayReporterMounted = false;

export function setApplePayReporterMounted(mounted: boolean): void {
	applePayReporterMounted = mounted;
}

export function isApplePayReporterMounted(): boolean {
	return applePayReporterMounted;
}

/**
 * A raw DOM Event rejected stackless by a third-party checkout script: nothing triage can act on (TEC-3664).
 * Matched by parsed shape because the serialization is engine-dependent: V8 gives `{"isTrusted":true}`, WebKit
 * and Gecko `{}`, and matching V8's string alone left the iOS-Safari half arriving.
 */
export function isStacklessEventRejection(name: string, message: string, stack: string | undefined): boolean {
	if (stack || name !== 'ObjectError') return false;
	let parsed: unknown;
	try {
		parsed = JSON.parse(message);
	} catch {
		return false;
	}
	if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return false;
	const keys = Object.keys(parsed);
	// A rejection with several fields carries real content and stays reportable.
	if (keys.length === 0) return true;
	return 'isTrusted' in parsed && typeof (parsed as { isTrusted: unknown }).isTrusted === 'boolean' && keys.length <= 3;
}

/**
 * A deliberate refusal (e.g. `claimPaymentToken` on a spent token), already in the server's wide event: the
 * client mirror of the reporter's `CLIENT_FAULT_RPC_CODES`. By shape, not message, so wording is not
 * load-bearing across layers; duck-typed so a stale bundle's `TRPCClientError` still matches.
 */
export function isClientFaultRpcError(error: unknown): boolean {
	if (typeof error !== 'object' || error === null) return false;
	const data = (error as { data?: { code?: unknown; httpStatus?: unknown } }).data;
	if (!data || typeof data !== 'object') return false;
	return data.code === 'FORBIDDEN' || data.httpStatus === 403;
}

/** #418 is the mismatch itself; the others are what React can report while recovering the same tree. */
const HYDRATION_ERROR_NUMBERS = new Set([418, 421, 422, 423, 425]);

export function isHydrationError(message: string): boolean {
	const number = /minified react error #(\d+)/i.exec(message)?.[1];
	if (number) return HYDRATION_ERROR_NUMBERS.has(Number(number));
	return message.toLowerCase().includes('hydrat');
}

/**
 * Whether this engine ignores an explicit `timeZone`, as timezone-spoofing automation that patches `Intl` does: a
 * genuine browser formats Madrid's midnight of 1 October 2026 as `01/10/26` in any system zone, and every date we
 * render with a fixed zone diverges from the server's (#418 `text`, TEC-4029). Fails open: no `Intl`, or a throw.
 */
export function isTimeZoneOptionIgnored(): boolean {
	try {
		const madrid = new Intl.DateTimeFormat('en-GB', {
			day: '2-digit',
			month: '2-digit',
			year: '2-digit',
			timeZone: 'Europe/Madrid',
		});
		return madrid.format(new Date(Date.UTC(2026, 8, 30, 22, 0))) !== '01/10/26';
	} catch {
		return false;
	}
}

/** Captured at module evaluation, before hydration and before a translator has touched the page. */
const initialDocumentLang = typeof document !== 'undefined' ? document.documentElement.lang : undefined;

/**
 * Chrome and Edge stamp `translated-ltr|rtl` on `<html>`, and every translator rewrites `<html lang>` to its
 * target. Translated text nodes and their `<font>` wrappers read as #418 `text` and `HTML` mismatches: the whole
 * TEC-3689 to TEC-3701 lineage was non-English browsers translating the English campaign page. Callers gate on
 * this at capture time, since `shouldReportFrontendError` also runs server-side with no DOM. A manual language
 * switch also reads as translated; harmless, because hydration precedes any switch.
 */
export function isDocumentTranslated(
	html: { className: string; lang: string } | undefined = typeof document !== 'undefined'
		? document.documentElement
		: undefined,
	arrivedLang: string | undefined = initialDocumentLang,
): boolean {
	if (!html) return false;
	if (/(?:^|\s)translated-(?:ltr|rtl)(?:\s|$)/.test(html.className)) return true;
	return !!arrivedLang && !!html.lang && html.lang !== arrivedLang;
}

export interface TextReplacement {
	readonly before: string;
	readonly after: string;
}

/** Long enough that no unrelated node collides, short enough to survive the monitor's truncation. */
const MIN_WITNESS_MATCH = 24;

/** U+00A0 is written as its escape: a literal one is invisible in a diff and one formatter pass from a space. */
function normalizeText(text: string): string {
	return text
		.replace(/\u00a0/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

function sharesPrefix(a: string, b: string, length: number): boolean {
	if (a.length < length || b.length < length) return false;
	for (let i = 0; i < length; i++) {
		if (a[i] !== b[i]) return false;
	}
	return true;
}

/**
 * Whether a translator, content blocker or WebView injection replaced text the app rendered. FAILS OPEN.
 * docs/notes/portals/frontend-error-filter.md
 */
export function wasOurTextReplaced(replacements: readonly TextReplacement[], witnesses: readonly string[]): boolean {
	if (replacements.length === 0 || witnesses.length === 0) return false;
	const normalizedWitnesses = witnesses.map(normalizeText).filter((text) => text.length >= MIN_WITNESS_MATCH);
	if (normalizedWitnesses.length === 0) return false;
	const isOurs = (text: string) =>
		normalizedWitnesses.some((witness) => sharesPrefix(text, witness, MIN_WITNESS_MATCH));
	return replacements.some((replacement) => {
		const before = normalizeText(replacement.before);
		if (before.length < MIN_WITNESS_MATCH) return false;
		// An `after` that is also ours is React repairing a real mismatch, which must keep reporting.
		return isOurs(before) && !isOurs(normalizeText(replacement.after));
	});
}

export function shouldReportFrontendError(name: string, message: string, stack?: string, cause?: string): boolean {
	// A chunk failing mid-render surfaces as React #520 with the fetch demoted to the cause (TEC-3740, TEC-3722).
	if (cause) {
		const causeHaystack = cause.toLowerCase();
		if (VERSION_SKEW_ERROR_PATTERNS.some((pattern) => causeHaystack.includes(pattern))) return false;
	}

	// A minified stack cannot tell a vendor overflow from ours; accepted, as ours would be deterministic (TEC-3592).
	if (
		name.toLowerCase().includes('rangeerror') &&
		message.toLowerCase().includes('maximum call stack size exceeded') &&
		isStackOverflowRecursionCycle(stack)
	) {
		return false;
	}

	if (isUnattributableThirdPartyError(stack)) return false;

	if (isStacklessEventRejection(name, message, stack)) return false;

	if (isReleasedQueryStreamReader(name, message, stack)) return false;

	const haystack = `${name} ${message}`.toLowerCase();
	return !NON_ACTIONABLE_ERROR_PATTERNS.some((pattern) => haystack.includes(pattern));
}
